/**
 * FOXGLOVE 可视化回放链接构造。
 *
 * 原先内联在 JobTaskList 里，感知报表页的 case 明细也要「打开可视化」，故提到共用位置。
 * 两个页面共用同一套 docker 池与取模规则，避免各自维护一份池子后漂移。
 *
 * mcap 对象 key 的形态固定为 ``<taskId>/<sceneName>.mcap``——taskId 是唯一入口，
 * 所以任何要下钻回放的列表都必须带上 taskId（scene_id 不够用）。
 */
import { getEvaluationFileUrls } from '@/apis/simulation';

/** player 服务的 docker 实例池。测试环境只部了 240 一台。 */
const TEST_DOCKER_IDS = ['240'];
const PROD_DOCKER_IDS = [
  '100', '101', '102', '103', '104', '105', '106', '107', '108', '109',
  '120', '121', '122', '123', '124', '125', '126', '127', '128', '129',
];

const PLAYER_BASE = 'https://player.brood.neolix.cn/foxglove';

/** 按当前 host 判环境，返回可用的 docker 实例池。 */
export const getFoxgloveDockerIds = (): string[] => {
  const host = window.location.hostname;
  const isTest = host.includes('test') || host === 'localhost' || host === '127.0.0.1';
  return isTest ? TEST_DOCKER_IDS : PROD_DOCKER_IDS;
};

/** 按 taskId 取模选实例：同一 task 始终落同一台，便于复现问题时定位。 */
export const pickFoxgloveId = (taskId: number): string => {
  const ids = getFoxgloveDockerIds();
  return ids[taskId % ids.length];
};

/** 拼 FOXGLOVE 播放器 URL（``ds=remote-file`` + mcap 直链）。 */
export const buildFoxgloveUrl = (mcapFileUrl: string, taskId: number): string => {
  const baseUrl = `${PLAYER_BASE}/${pickFoxgloveId(taskId)}/`;
  const params = new URLSearchParams({
    ds: 'remote-file',
    'ds.url': mcapFileUrl,
  });
  return `${baseUrl}?${params.toString()}`;
};

/** task 的 mcap 对象 key。 */
export const buildMcapKey = (taskId: number, sceneName: string): string =>
  `${taskId}/${sceneName}.mcap`;

/**
 * 取 task 的 mcap 直链。没有对应文件时返回 ``null``（由调用方决定提示文案）。
 */
export const resolveMcapUrl = async (
  taskId: number,
  sceneName: string,
): Promise<string | null> => {
  const files = await getEvaluationFileUrls([buildMcapKey(taskId, sceneName)]);
  return files.find((f) => f.url)?.url || null;
};
